import React from 'react';
import { TOUR } from './landingCopy';
import { TOUR_IMAGES } from './landingAssets';
import ProductShot from './ProductShot';

const ARC = 'M 108.4 32.7 A 70 70 0 1 1 91.6 32.7';
const STONE = '#B8B5AD';

// The tab icon is the Zoea mark with one part emphasised: ring (Organiseren), stem (Verbinden), dot (Creëren).
const TabIcon = ({ pillar }) => (
  <svg viewBox="0 0 200 200" fill="none" aria-hidden="true">
    <path d={ARC} stroke={pillar === 'org' ? 'currentColor' : STONE} strokeWidth="12" strokeLinecap="round" />
    <path d="M 100 30 L 100 12" stroke={pillar === 'ver' ? 'currentColor' : STONE} strokeWidth={pillar === 'ver' ? 14 : 12} strokeLinecap="round" />
    <circle cx="100" cy="8" r={pillar === 'cre' ? 14 : 9} fill={pillar === 'cre' ? '#D4A574' : STONE} />
  </svg>
);

const Facts = ({ facts }) => (facts ? <ul className="zl-facts">{facts.map((fact) => <li key={fact}>{fact}</li>)}</ul> : null);

const Copy = ({ data }) => (
  <div className="zl-copy">
    <p className="zl-eyebrow">{data.eyebrow}</p>
    <h3>{data.title}</h3>
    <p>{data.text}</p>
    <Facts facts={data.facts} />
  </div>
);

const WideCopy = ({ data }) => (
  <div className="zl-copy">
    <div className="zl-lead-col">
      <p className="zl-eyebrow">{data.eyebrow}</p>
      <h3>{data.title}</h3>
    </div>
    <div className="zl-lead-col">
      <p>{data.text}</p>
      <Facts facts={data.facts} />
    </div>
  </div>
);

// Short text-only blocks for functionality that does not need a screenshot of its own.
const Minis = ({ items }) => (
  <div className="zl-minis">
    {items.map((item) => (
      <div key={item.title} className="zl-mini">
        <p className="zl-eyebrow">{item.eyebrow}</p>
        <h4>{item.title}</h4>
        <p>{item.text}</p>
      </div>
    ))}
  </div>
);

const Also = ({ data }) => (
  <p className="zl-also"><b>{data.label}</b> {data.text}</p>
);

const NextLink = ({ to, label, onSelect }) => (
  <div className="zl-next">
    <button type="button" className="zl-linkbtn" data-goto={to} onClick={() => onSelect(to, { scroll: true, focus: true })}>{label}</button>
  </div>
);

const ProductTour = ({ tab, onSelect }) => {
  const keys = TOUR.tabs.map((t) => t.key);

  const onKeyDown = (event, index) => {
    const move = { ArrowRight: (index + 1) % keys.length, ArrowLeft: (index - 1 + keys.length) % keys.length, Home: 0, End: keys.length - 1 }[event.key];
    if (move === undefined) return;
    event.preventDefault();
    onSelect(keys[move], { focus: true });
  };

  return (
    <section className="zl-tour zl-wrap" id="tour" aria-labelledby="tour-title">
      <div className="zl-tour-head">
        <p className="zl-eyebrow">{TOUR.eyebrow}</p>
        <h2 id="tour-title" className="zl-h2">{TOUR.title}</h2>
        <p>{TOUR.intro}</p>
      </div>

      <div className="zl-tablist" role="tablist" aria-label={TOUR.tabsLabel}>
        {TOUR.tabs.map((t, index) => (
          <button
            key={t.key}
            type="button"
            className="zl-tab"
            role="tab"
            id={`tab-${t.key}`}
            aria-controls={`panel-${t.key}`}
            aria-selected={tab === t.key}
            tabIndex={tab === t.key ? 0 : -1}
            onClick={() => onSelect(t.key, {})}
            onKeyDown={(event) => onKeyDown(event, index)}
          >
            <span className="zl-t-name"><TabIcon pillar={t.key} />{t.name}</span>
            <span className="zl-t-sub">{t.sub}</span>
          </button>
        ))}
      </div>

      <div className="zl-panel" role="tabpanel" id="panel-org" aria-labelledby="tab-org" tabIndex={0} hidden={tab !== 'org'}>
        <article className="zl-scene zl-wide">
          <WideCopy data={TOUR.org.calendar} />
          <div className="zl-media">
            <div className="zl-only-desk"><ProductShot image={TOUR_IMAGES.kalender} /></div>
            <div className="zl-only-mob">
              <ProductShot image={TOUR_IMAGES.kalenderMobiel} variant="pan" panWidth={500} panLabel={TOUR.panLabel} />
              <p className="zl-pan-hint">{TOUR.panAction}</p>
            </div>
          </div>
        </article>
        <Minis items={TOUR.org.minis} />
        <Also data={TOUR.org.also} />
        <NextLink to="ver" label={TOUR.org.next} onSelect={onSelect} />
      </div>

      <div className="zl-panel" role="tabpanel" id="panel-ver" aria-labelledby="tab-ver" tabIndex={0} hidden={tab !== 'ver'}>
        <article className="zl-scene zl-only-mob">
          <Copy data={TOUR.ver.serviceMobile} />
          <div className="zl-media"><ProductShot image={TOUR_IMAGES.dienstMobiel} variant="white" className="zl-mobcrop" /></div>
        </article>
        <article className="zl-scene zl-only-desk">
          <Copy data={TOUR.ver.serviceDesktop} />
          <div className="zl-media"><ProductShot image={TOUR_IMAGES.dienst} variant="white" /></div>
        </article>
        <Minis items={TOUR.ver.minis} />
        <Also data={TOUR.ver.also} />
        <NextLink to="cre" label={TOUR.ver.next} onSelect={onSelect} />
      </div>

      <div className="zl-panel" role="tabpanel" id="panel-cre" aria-labelledby="tab-cre" tabIndex={0} hidden={tab !== 'cre'}>
        <article className="zl-scene zl-wide zl-feature">
          <WideCopy data={TOUR.cre.writer} />
          <div className="zl-media">
            <div className="zl-only-desk"><ProductShot image={TOUR_IMAGES.writer} /></div>
            <div className="zl-only-mob"><ProductShot image={TOUR_IMAGES.writerMobiel} className="zl-mobcrop" /></div>
          </div>
        </article>
        <article className="zl-scene zl-phone-scene">
          <Copy data={TOUR.cre.book} />
          <div className="zl-media"><ProductShot image={TOUR_IMAGES.boek} variant="phone" /></div>
        </article>
        <Also data={TOUR.cre.also} />
        <div className="zl-next"><a className="zl-linkbtn" href="#kennismaking">{TOUR.cre.next}</a></div>
      </div>
    </section>
  );
};

export default ProductTour;
